import React, { useCallback, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLaptop, faBell, faBatteryQuarter, faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons'
import BizPlusPage from '../../components/biz_plus/biz_plus'
import { useAppSelector } from '../../hooks'
import {
  getStaleDetectionWarningMessage,
  shouldShowStaleDetectionWarning,
} from '../../helpers/staleDetectionWarning'
import { getFromNowAsText } from '../../helpers/timeHelper'
import useDashboardMamorios from './useDashboardMamorios.hook'
import { GROUPS, OVERVIEW } from '../../constants/Paths'
import './DashboardDemo.css'

type DashboardFilter = 'all' | 'missing' | 'battery' | 'away'

const hasLowBattery = (mamorio: IMamorio) => (
  mamorio.estimated_remain_battery !== null
  && mamorio.estimated_remain_battery < 10
)

const DashboardDemoPage = () => {
  const [filter, setFilter] = useState<DashboardFilter>('all')
  const { group_id } = useParams<{ group_id?: string }>()
  const groups = useAppSelector((state) => state.team.groups)
  const {
    mamorios, loading, error, loadMamorios,
  } = useDashboardMamorios(group_id, groups)
  const dataSource = useMemo(() => ({ mamorios, loadMamorios }), [mamorios, loadMamorios])
  const groupsById = useMemo(() => new Map(groups?.map((group) => [String(group.id), group])), [groups])
  const hasWarning = useCallback((mamorio: IMamorio) => (
    shouldShowStaleDetectionWarning(mamorio.recent_log?.at, groupsById.get(String(mamorio.group_id)))
  ), [groupsById])
  const filterMamorio = useCallback((mamorio: IMamorio) => (
    filter === 'all'
    || (filter === 'missing' && hasWarning(mamorio))
    || (filter === 'battery' && hasLowBattery(mamorio))
    || (filter === 'away' && mamorio.is_rent)
  ), [filter, hasWarning])
  const filters: {
    id: DashboardFilter
    title: string
    icon: typeof faLaptop
    count: number
    note: string
  }[] = [
    {
      id: 'all', title: '総デバイス', icon: faLaptop, count: mamorios.length, note: '登録済みのすべてのデバイス',
    },
    {
      id: 'missing', title: '未検知警告中', icon: faBell, count: mamorios.filter(hasWarning).length, note: 'グループの警告設定に基づく',
    },
    {
      id: 'battery', title: '電池切れ・残りわずか', icon: faBatteryQuarter, count: mamorios.filter(hasLowBattery).length, note: '推定電池残量が10%未満',
    },
    {
      id: 'away', title: '持出中', icon: faSignOutAlt, count: mamorios.filter((mamorio) => mamorio.is_rent).length, note: '持出中のデバイスを確認',
    },
  ]
  const alerts = mamorios.filter((mamorio) => (
    hasWarning(mamorio) || hasLowBattery(mamorio) || mamorio.is_rent
  ))

  let emptyStateMessage = '現在、表示する通知はありません。'
  if (loading) emptyStateMessage = '読み込み中…'
  if (error) emptyStateMessage = 'イベントログを取得できませんでした。'
  const alertDotClass = (mamorio: IMamorio) => {
    if (hasWarning(mamorio)) return 'demo-alert-dot--danger'
    return hasLowBattery(mamorio) ? 'demo-alert-dot--warning' : ''
  }

  return (
    <BizPlusPage
      dataSource={dataSource}
      collapsibleFilters
      filterMamorio={filterMamorio}
      beforeMap={(
        <>
          <div className="demo-scope">
            <div>
              <h1>{group_id ? 'グループのダッシュボード' : 'チーム全体のダッシュボード'}</h1>
              <p>{group_id ? 'このグループのMAMORIOを表示しています。' : 'アクセスできるすべてのグループのMAMORIOを表示しています。'}</p>
            </div>
            <div className="demo-scope-links">
              <Link to={group_id ? `${GROUPS}/${group_id}` : OVERVIEW}>一覧を開く</Link>
            </div>
          </div>
          {error && (
            <p className="demo-load-error" role="alert">
              {error}
              {' '}
              画面右上の更新ボタンで再試行できます。
            </p>
          )}
          {loading && <p className="demo-load-status" role="status">MAMORIOを読み込んでいます…</p>}
          <section className="demo-kpis" aria-label="デバイスの状態で絞り込み">
            {filters.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`demo-kpi demo-kpi--${item.id}`}
                aria-pressed={filter === item.id}
                disabled={loading || Boolean(error)}
                onClick={() => setFilter(item.id)}
              >
                <span className="demo-kpi-icon"><FontAwesomeIcon icon={item.icon} /></span>
                <span>
                  <span className="demo-kpi-title">{item.title}</span>
                  <span className="demo-kpi-value">
                    {loading || error ? '—' : item.count}
                    <small>台</small>
                  </span>
                  <span className="demo-kpi-note">{item.note}</span>
                </span>
              </button>
            ))}
          </section>
        </>
      )}
      renderMapAside={(focusMamorio) => (
        <section className="demo-alerts demo-panel" aria-label="イベントログ">
          <header className="demo-panel-heading">
            <h2>
              <FontAwesomeIcon icon={faBell} />
              {' '}
              イベントログ
            </h2>
            <span>
              {loading || error ? '—' : alerts.length}
              件
            </span>
          </header>
          {loading || error || alerts.length === 0 ? (
            <p className="demo-alert-empty">{emptyStateMessage}</p>
          ) : (
            <ul>
              {alerts.map((mamorio) => (
                <li key={mamorio.id}>
                  <span className={`demo-alert-dot ${alertDotClass(mamorio)}`} />
                  <div>
                    <div className="demo-alert-title">
                      <strong>{mamorio.name}</strong>
                      <small>{mamorio.recent_log ? getFromNowAsText(mamorio.recent_log.at) : '未検知'}</small>
                    </div>
                    {!group_id && <p className="demo-alert-group">{groupsById.get(String(mamorio.group_id))?.name}</p>}
                    {hasWarning(mamorio) && <p>{getStaleDetectionWarningMessage(groupsById.get(String(mamorio.group_id)))}</p>}
                    {mamorio.is_rent && <p>持出中</p>}
                    {hasLowBattery(mamorio) && (
                      <p>
                        推定電池残量
                        {mamorio.estimated_remain_battery}
                        %・電池の交換を推奨
                      </p>
                    )}
                    <button
                      type="button"
                      className="demo-action"
                      disabled={!mamorio.recent_log}
                      onClick={() => {
                        setFilter('all')
                        focusMamorio(mamorio)
                      }}
                    >
                      マップで確認 →
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    />
  )
}

export default DashboardDemoPage
